import React, { useState } from 'react';
import { downloadInvoicePdf } from '../../utils/downloadInvoicePdf';
import { useDirectInvoices } from '../../hooks/useDirectInvoices';
import InvoiceStatusChip from '../common/InvoiceStatusChip';
import { formatKr } from '../../utils/formatKr';
import { COLORS, FONTS } from '../../styles/tokens';

// Gebyrfakturaer fra Tippi til sjåføren (Inntekt-siden). Ingen betalingshandlinger: admin merker betalt.
export default function DriverFeeSection() {
  const { invoices } = useDirectInvoices('driver');
  const [busyId, setBusyId] = useState(null);
  const [error, setError]   = useState('');

  const fees = invoices.filter(i => i.kind === 'tippi-fee');
  if (fees.length === 0) return null; // også mens den laster: ingen «Laster…»-hopp
  const overdue = fees.some(f => f.status === 'overdue');

  async function download(inv) {
    if (busyId) return;
    setError('');
    setBusyId(inv.id);
    try {
      await downloadInvoicePdf(inv.id);
    } catch (e) {
      setError(e?.message || 'Noe gikk galt. Prøv igjen.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section style={styles.card} aria-label="Tippi-gebyr">
      <h2 style={styles.title}>Tippi-gebyr</h2>

      {overdue && (
        <p role="alert" style={styles.error}>
          Gebyr fra Tippi er forfalt. Du kan ikke ta nye fakturaoppdrag før det er betalt. Kortoppdrag påvirkes ikke.
        </p>
      )}
      {error && <p role="alert" style={styles.error}>{error}</p>}

      {fees.map(inv => {
        const due = inv.dueDate?.toDate?.();
        return (
          <div key={inv.id} style={styles.row} data-testid={`fee-${inv.id}`}>
            <div style={styles.top}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.number}>{inv.periodText || inv.period}</div>
                {due && (
                  <div style={styles.meta}>
                    Forfall {due.toLocaleDateString('nb-NO', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={styles.amount}>{formatKr(inv.grossAmount)}</div>
                <InvoiceStatusChip invoice={inv} />
              </div>
            </div>
            <div style={styles.actions}>
              <button type="button" style={styles.btn} disabled={!!busyId}
                      aria-label={`Last ned PDF for ${inv.periodText || inv.period}`}
                      onClick={() => download(inv)}>Last ned PDF</button>
            </div>
          </div>
        );
      })}
    </section>
  );
}

const styles = {
  card:    { background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 20, padding: 18, marginBottom: 14 },
  title:   { fontFamily: FONTS.display, fontSize: 24, fontWeight: 700, margin: '0 0 4px', color: COLORS.textDark },
  error:   { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '10px 12px', borderRadius: 10, fontSize: 14, margin: '8px 0' },
  row:     { padding: '14px 0', borderBottom: `1px solid ${COLORS.border}` },
  top:     { display: 'flex', alignItems: 'flex-start', gap: 12 },
  number:  { fontWeight: 700, fontSize: 15, color: COLORS.textDark },
  meta:    { fontSize: 12.5, color: COLORS.textMid, marginTop: 2 },
  amount:  { fontFamily: FONTS.display, fontWeight: 700, fontSize: 22, lineHeight: 1, marginBottom: 5, color: COLORS.textDark },
  actions: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 },
  btn:     { borderRadius: 20, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', background: COLORS.white, border: `1px solid ${COLORS.border}`, color: COLORS.textDark },
};
